import type { KindReason, MailPlanSenderKindOutput, MessageKind, SenderMatch } from "@kwp/contracts";
import { useCallback, useEffect, useId, useState, type ReactNode } from "react";
import { errorMessage, useTRPCClient } from "../../lib/api";
import { Button, ErrorText } from "../../ui";
import { Icon } from "../../ui/icons";
import { Dialog } from "../../ui/overlay";
import { useToast } from "../../ui/toast";
import { newRequestId } from "../tasks/use-task-actions";
import { kindLabel, kindReasonText, KIND_VIEWS } from "./kinds";
import { senderAddress, type Thread } from "./model";

/**
 * "This sender is…" (architecture §6.7, WP-063): one correction per sender, for the address or
 * the whole domain, fixes the sender's past and future mail. The dialog shows what would move
 * first (`mail.planSenderKind`), and applies exactly that plan (`actions.applyPlan`), with Undo.
 */
export const SENDER_KIND_LABELS: Record<MessageKind, string> = {
  person: "A person",
  notification: "Notifications",
  newsletter: "Newsletters",
  receipt: "Receipts",
};

/** Whose mail a conversation is for a correction: its newest sender unless that is the mailbox. */
export function threadSender(thread: Pick<Thread, "lastFrom" | "participants">, ownAddress: string | null): string | null {
  const own = ownAddress === null ? null : ownAddress.toLowerCase();
  if (thread.lastFrom !== null && thread.lastFrom.email.toLowerCase() !== own && thread.lastFrom.email !== "") return thread.lastFrom.email;
  return senderAddress(thread.participants, ownAddress);
}

export const domainOfAddress = (address: string) => address.slice(address.lastIndexOf("@") + 1).toLowerCase();

/** Applying a planned sender change, and undoing what it did. */
function useApplySenderKind(onDone: () => void) {
  const client = useTRPCClient();
  const toast = useToast();
  return useCallback(
    async (planId: string, message: string) => {
      const outcome = await client.actions.applyPlan.mutate({ planId });
      onDone();
      if (outcome.status !== "done") return;
      const auditId = outcome.auditId;
      toast({
        message,
        ...(auditId === null
          ? {}
          : {
              action: {
                label: "Undo",
                onClick: () =>
                  void client.audit.undo.mutate({ auditId }).then(
                    () => toast({ message: "Undone." }),
                    (e: unknown) => toast({ tone: "error", message: errorMessage(e) }),
                  ),
              },
            }),
      });
    },
    [client, toast, onDone],
  );
}

/** The plan for one choice, read again whenever the choice changes. */
function usePlan(match: SenderMatch, value: string, kind: MessageKind | null | undefined) {
  const client = useTRPCClient();
  const [plan, setPlan] = useState<MailPlanSenderKindOutput | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setPlan(null);
    setError(null);
    if (kind === undefined) return undefined;
    let live = true;
    client.mail.planSenderKind.mutate({ match, value, kind }).then(
      (p) => live && setPlan(p),
      (e: unknown) => live && setError(errorMessage(e)),
    );
    return () => {
      live = false;
    };
  }, [client, match, value, kind]);
  return { plan, error };
}

/** Keys typed in these dialogs stay there: the inbox's shortcuts listen on the document. */
function KeysStayHere({ children }: { children: ReactNode }) {
  return <div onKeyDown={(e) => e.stopPropagation()}>{children}</div>;
}

const moves = (p: MailPlanSenderKindOutput) =>
  p.matched === 0
    ? "No cached mail moves; new mail follows your choice."
    : `${p.matched} message${p.matched === 1 ? "" : "s"} in ${p.threads} conversation${p.threads === 1 ? "" : "s"} will move.`;

/** The four kinds as a choice, for the address or the domain, with the count before applying. */
export function SenderKindDialog({
  address,
  initialKind,
  reason = null,
  onClose,
}: {
  address: string;
  /** Preselected (the row's inline choice); undefined asks first. */
  initialKind?: MessageKind;
  /** "Why here?": the conversation's reason, said first. */
  reason?: KindReason | null;
  onClose: () => void;
}) {
  const id = useId();
  const [match, setMatch] = useState<SenderMatch>("address");
  const [kind, setKind] = useState<MessageKind | undefined>(initialKind);
  const value = match === "address" ? address.toLowerCase() : domainOfAddress(address);
  const { plan, error } = usePlan(match, value, kind);
  const [failed, setFailed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const apply = useApplySenderKind(onClose);
  return (
    <KeysStayHere>
      <Dialog title="This sender is…" {...(reason === null ? {} : { description: `Why here? ${kindReasonText(reason)}` })} onClose={onClose}>
        <div className="flex flex-col gap-3">
          <fieldset className="flex flex-col gap-1">
            <legend className="text-sm font-medium text-text">For</legend>
            {(["address", "domain"] as const).map((m) => (
              <label key={m} className="flex min-h-9 items-center gap-2 text-sm text-text">
                <input type="radio" name={`${id}-match`} checked={match === m} onChange={() => setMatch(m)} className="accent-accent" />
                {m === "address" ? address.toLowerCase() : `Everyone at ${domainOfAddress(address)}`}
              </label>
            ))}
          </fieldset>
          <fieldset className="grid grid-cols-2 gap-2">
            <legend className="mb-1 text-sm font-medium text-text">Is</legend>
            {KIND_VIEWS.map((k) => (
              <label
                key={k.key}
                className="flex min-h-9 cursor-pointer items-center gap-2 rounded-md border border-border px-2 text-sm text-text has-[:checked]:border-accent"
              >
                <input type="radio" name={`${id}-kind`} checked={kind === k.key} onChange={() => setKind(k.key)} className="accent-accent" />
                <Icon name={k.icon} className="text-muted" />
                {SENDER_KIND_LABELS[k.key]}
              </label>
            ))}
          </fieldset>
          {kind === undefined ? null : plan === null && error === null ? <p className="text-sm text-muted">Counting…</p> : null}
          {plan === null ? null : <p className="text-sm text-text">{moves(plan)}</p>}
          {error === null ? null : <ErrorText>{error}</ErrorText>}
          {failed === null ? null : <ErrorText>{failed}</ErrorText>}
          <div className="flex justify-end gap-2">
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="primary"
              disabled={plan === null || busy}
              onClick={() => {
                if (plan === null || kind === undefined) return;
                setBusy(true);
                apply(plan.planId, `Mail from ${value} now goes to ${kind === "person" ? "People" : SENDER_KIND_LABELS[kind]}.`).catch((e: unknown) => {
                  setBusy(false);
                  setFailed(errorMessage(e));
                });
              }}
            >
              Apply
            </Button>
          </div>
        </div>
      </Dialog>
    </KeysStayHere>
  );
}

/** Settings → Senders: removing an override, with the count first. */
export function RemoveSenderKindDialog({ match, value, onClose }: { match: SenderMatch; value: string; onClose: () => void }) {
  const { plan, error } = usePlan(match, value, null);
  const [failed, setFailed] = useState<string | null>(null);
  const apply = useApplySenderKind(onClose);
  const who = match === "domain" ? `everyone at ${value}` : value;
  return (
    <Dialog title="Remove this choice?" description={`Mail from ${who} will be sorted by its headers again.`} onClose={onClose}>
      <div className="flex flex-col gap-3">
        {plan === null && error === null ? <p className="text-sm text-muted">Counting…</p> : null}
        {plan === null ? null : <p className="text-sm text-text">{moves(plan)}</p>}
        {error === null ? null : <ErrorText>{error}</ErrorText>}
        {failed === null ? null : <ErrorText>{failed}</ErrorText>}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={plan === null}
            onClick={() => plan !== null && apply(plan.planId, `Removed your choice for ${who}.`).catch((e: unknown) => setFailed(errorMessage(e)))}
          >
            Remove
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

/**
 * Claude's suggestion in the conversation (WP-063, WP-065): Undo makes the sender a person's (a normal override, so
 * Claude does not ask again); Keep makes the suggestion the owner's own. Both act at once: the
 * change is the one the strip already shows, undone.
 */
export function useSuggestionAnswer() {
  const client = useTRPCClient();
  const toast = useToast();
  return useCallback(
    async (address: string, kind: MessageKind) => {
      try {
        await client.mail.setSenderKind.mutate({ requestId: newRequestId(), input: { match: "address", value: address.toLowerCase(), kind } });
        toast({ message: kind === "person" ? `Moved back to People: ${address}.` : `Kept: ${address} stays in ${SENDER_KIND_LABELS[kind]}.` });
      } catch (e) {
        toast({ tone: "error", message: errorMessage(e) });
      }
    },
    [client, toast],
  );
}

/**
 * The conversation's sender strip (WP-065), under the toolbar: a New sender's four-way choice
 * (each opens "This sender is…" with that kind preselected; the buttons wrap on a phone), or
 * Claude's suggestion with Undo and Keep, which act at once. Nothing for any other conversation.
 */
export function SenderStrip({
  address,
  newSender,
  kind,
  suggested,
  onChoose,
}: {
  address: string;
  newSender: boolean;
  /** Where the conversation is now (Keep keeps it there). */
  kind: MessageKind;
  /** Claude moved this sender (`kindReason == "suggested"`). */
  suggested: boolean;
  onChoose: (kind: MessageKind) => void;
}) {
  const answer = useSuggestionAnswer();
  const strip = "flex flex-wrap items-center gap-2 border-b border-border py-2 text-sm text-text";
  if (suggested) {
    return (
      <div role="group" aria-label={`Claude's suggestion for ${address}`} className={strip}>
        <span className="min-w-0 flex-1">Claude moved this sender to {kindLabel(kind)}.</span>
        <Button className="px-2" onClick={() => void answer(address, "person")}>
          Undo
        </Button>
        <Button className="px-2" onClick={() => void answer(address, kind)}>
          Keep
        </Button>
      </div>
    );
  }
  if (!newSender) return null;
  return (
    <div role="group" aria-label={`This sender is… (${address})`} className={strip}>
      <span className="min-w-0 basis-full sm:basis-auto">
        First mail from <span className="break-all font-medium">{address}</span>. This sender is…
      </span>
      {KIND_VIEWS.map((k) => (
        <Button key={k.key} className="px-2" onClick={() => onChoose(k.key)}>
          <Icon name={k.icon} className="text-muted" />
          {SENDER_KIND_LABELS[k.key]}
        </Button>
      ))}
    </div>
  );
}
